import { TripAnnotation } from "@fairfleet/geotab";

/**
 * How far ahead of a trip start a stray annotation is still attributed to that trip. The app
 * annotates a trip at its provisional start; when Geotab later moves the start (re-segmentation)
 * the annotation ends up before the final window. Real data of a customer database shows offsets of up to
 * five minutes, so ten minutes leaves headroom.
 */
export const ANNOTATION_MAX_LEAD_MS = 10 * 60 * 1000;

interface TripWindow {
  id: string;
  start: Date;
  stop: Date;
}

/**
 * Assigns each trip of one device its annotation. An annotation inside `[start, stop]` belongs
 * to that trip, and the latest one inside wins (the app keeps the newest and removes older
 * duplicates). A trip without one takes the latest stray annotation that lies between the
 * previous trip and its own start, at most `ANNOTATION_MAX_LEAD_MS` ahead of it.
 */
export function assignAnnotations<T extends TripWindow>(
  trips: T[],
  annotations: TripAnnotation[],
  maxLeadMs = ANNOTATION_MAX_LEAD_MS,
): Map<string, TripAnnotation> {
  const sortedTrips = [...trips].sort((a, b) => a.start.getTime() - b.start.getTime());
  const sorted = [...annotations].sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
  const result = new Map<string, TripAnnotation>();

  sortedTrips.forEach((trip, index) => {
    const inside = latestInWindow(sorted, trip.start.getTime(), trip.stop.getTime());
    if (inside) {
      result.set(trip.id, inside);
      return;
    }
    const previousStop = index > 0 ? sortedTrips[index - 1].stop.getTime() : Number.NEGATIVE_INFINITY;
    const from = Math.max(previousStop + 1, trip.start.getTime() - maxLeadMs);
    const stray = latestInWindow(sorted, from, trip.start.getTime() - 1);
    if (stray) result.set(trip.id, stray);
  });

  return result;
}

/** The latest annotation with `from <= dateTime <= to`; `sorted` is ordered by dateTime. */
function latestInWindow(sorted: TripAnnotation[], from: number, to: number): TripAnnotation | undefined {
  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (sorted[mid].dateTime.getTime() < from) low = mid + 1;
    else high = mid;
  }
  let latest: TripAnnotation | undefined;
  for (let i = low; i < sorted.length && sorted[i].dateTime.getTime() <= to; i++) {
    latest = sorted[i];
  }
  return latest;
}
